import { PIPELINES, useGetCombinedPipelinesAndStagesQuery } from '@/features/pipelines/pipelineApi';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import React, { createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

const INITIAL_STATE = {
	pipelines: PIPELINES.map((p) => ({ id: p })),
	stages: [],
	active: [],
	inactive: [],
	closed: [],
	completed: [],
	endorsements: [],
	checkEndorsementStage: (stage) => false,
	isLoading: true,
	isError: false,
	isSuccess: false,
	isUninitialized: true,
};

// Create initial context structure
const initialContext = PIPELINES.reduce(
	(acc, pId) => {
		acc[pId] = {
			...INITIAL_STATE,
			pipeline: { id: pId },
		};
		return acc;
	},
	{ undefined: INITIAL_STATE },
);

const PipelineContext = createContext(initialContext);

export const usePipelineContext = (pipelineId) => {
	const context = useContext(PipelineContext);
	if (!context) {
		throw new Error('usePipelineContext must be used within a PipelineContextProvider');
	}
	return pipelineId && context?.[pipelineId] ? context?.[pipelineId] : context.undefined;
};

export const PipelineContextProvider = ({ children }) => {
	const { username: email } = useSelector((state) => state.msalAccount.account);

	const { data: userDetails } = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		},
	);

	const { data, isLoading, isError, isSuccess, isUninitialized } =
		useGetCombinedPipelinesAndStagesQuery(
			{ brokerGroupIds: userDetails?.brokerGroupArray },
			{
				skip: !userDetails,
				selectFromResult: ({ data, ...rest }) => {
					return {
						...rest,
						data: data ?? initialContext,
					};
				},
			},
		);

	const checkEndorsementStage = useCallback(
		(stageId) => {
			const currentStage = data.undefined?.stages?.find((s) => s.id === stageId);
			return currentStage?.label?.toLowerCase()?.includes('endorsement');
		},
		[data.undefined?.stages],
	);

	const contextValue = useMemo(() => {
		const withStatus = Object.fromEntries(
			Object.entries(data).map(([key, value]) => [
				key,
				{
					...value,
					checkEndorsementStage,
					isLoading,
					isError,
					isSuccess,
					isUninitialized,
				},
			]),
		);

		return withStatus;
	}, [data, checkEndorsementStage, isLoading, isError, isSuccess, isUninitialized]);

	useEffect(() => {
		console.log('PIPELINE CONTEXT', contextValue);
	}, [contextValue]);

	return <PipelineContext.Provider value={contextValue}>{children}</PipelineContext.Provider>;
};

PipelineContextProvider.displayName = 'PipelineContextProvider';
